import { TooltipTrigger } from '@toggle/design-system';
import { startOfDay, subWeeks } from 'date-fns';
import React, { useMemo, useState } from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { parseEarningsInsightFromEvents } from '~/react-query-hooks/events/use-event-feed/earnings-from-events';
import { useEventFeed } from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { CompanyCatalystCounterTooltip } from '~/views/dashboard/my-assets-widget/assets-table/insights-cell/company-catalyst-counter-tooltip/CompanyCatalystCounterTooltip';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import * as S from './RevampCompetitorsSection.styles';

const MAX_COMPANY_CATALYSTS = 2;

export interface CompetitorCatalystsCellProps {
  entityTag: string;
  source: PosthogTrackingSource;
}

export const CompetitorCatalystsCell = ({
  entityTag,
  source,
}: CompetitorCatalystsCellProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const canShowCompanyCatalysts = !isBasicOrLightUser;

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data: eventFeedData, isFetching: isEarningsFeedFetching } =
    useEventFeed(
      {
        entities: [entityTag],
        type: 'earnings-insight',
        date_from: fromDate.toISOString(),
      },
      { enabled: !isBasicOrLightUser }
    );

  const { data: catalystEventFeedData, isFetching: isCatalystsFetching } =
    useEventFeed(
      {
        entities: [entityTag],
        type: 'attribution-insight',
        date_from: fromDate.toISOString(),
        filters: {
          company: {
            values: [
              { category: 'corporate_actions_strategic_announcements' },
              { category: 'management_leadership_changes' },
              { category: 'financial_operational_updates' },
              { category: 'legal_regulatory_events' },
            ],
          },
        },
      },
      { enabled: canShowCompanyCatalysts }
    );

  const earningsInsightWithEvent = useMemo(() => {
    const events = eventFeedData?.pages?.flatMap(page => page.events) ?? [];
    return parseEarningsInsightFromEvents(events);
  }, [eventFeedData?.pages]);

  const catalystEvents = useMemo(
    () => catalystEventFeedData?.pages?.flatMap(page => page.events) ?? [],
    [catalystEventFeedData?.pages]
  );

  const isLoading = isEarningsFeedFetching || isCatalystsFetching;

  if (isLoading) {
    return <S.StyledSkeletonLoader />;
  }

  const exceedsMaxCatalysts = catalystEvents.length > MAX_COMPANY_CATALYSTS;
  const visibleCatalystEvents = catalystEvents.slice(0, MAX_COMPANY_CATALYSTS);

  const remainingCatalystEvents = exceedsMaxCatalysts
    ? catalystEvents.slice(MAX_COMPANY_CATALYSTS)
    : [];

  return (
    <S.InsightsContainer>
      {earningsInsightWithEvent ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsightWithEvent.earningsInsight}
          eventId={earningsInsightWithEvent.eventId}
          read={earningsInsightWithEvent.read}
          width={18}
          height={20}
          source={source}
        />
      ) : (
        <S.Placeholder />
      )}
      {visibleCatalystEvents.map(
        event =>
          event.metadata.category && (
            <CatalystInsightIcon
              key={event.source_id}
              catalystInsightId={event.source_id}
              subCategory={event.metadata.sub_category ?? undefined}
              isBordered
              customSize={{ width: 18, height: 20 }}
              identifier={entityTag}
              source={source}
              eventId={event.id}
              read={event.read}
              category={event.metadata.category}
            />
          )
      )}
      {!!remainingCatalystEvents.length && (
        <TooltipContainer
          label={
            <CompanyCatalystCounterTooltip
              events={remainingCatalystEvents}
              source={source}
              onClose={() => setIsTooltipOpen(false)}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          isOpen={isTooltipOpen}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <S.InsightCount>+{remainingCatalystEvents.length}</S.InsightCount>
        </TooltipContainer>
      )}
    </S.InsightsContainer>
  );
};
